/**
 * Workflow Service
 * Handles workflow-related API operations
 */

import { graphqlClient } from '../core/graphqlClient';
import { apiClient } from '../core/apiClient';
import { 
  GET_WORKFLOWS_DASHBOARD, 
  SEARCH_WORKFLOWS_DASHBOARD,
  SEARCH_WORKFLOWS_DASHBOARD_BY_PROGRAM,
  SEARCH_WORKFLOWS_DASHBOARD_BY_USER_GROUP,
  SEARCH_WORKFLOWS_DASHBOARD_BY_USER_GROUPS,
  GET_TASKS_DASHBOARD, 
  SEARCH_TASKS_DASHBOARD,
  GET_WORKFLOW_STATUSES_DASHBOARD,
  SEARCH_WORKFLOW_STATUSES_DASHBOARD,
  GET_WORKFLOW_STATUS_BY_ID,
  GET_PROGRAM_TYPES,
  GET_ALL_WORKFLOWS_FOR_DROPDOWN,
  GET_ALL_TASKS_FOR_DROPDOWN,
  GET_ALL_WORKFLOW_STATUSES_FOR_DROPDOWN,
  GET_USER_ROLES,
  GET_PROGRAM_TYPE_STATUSES,
  GET_PROGRAM_TYPE_DASHBOARD,
  GET_WORKFLOW_AUDIT_LOG,
  GET_PROGRAM_STATUS_AUDIT_LOG,
  GET_PROGRAM_TYPE_AUDIT_LOG,
  GET_WORKFLOW_STATUS_AUDIT_LOG,
  GET_TASK_AUDIT_LOG,
} from '../resources/workflowGquery';

export interface WorkflowDashboard {
  workflow_id: number;
  workflow_name: string;
  program_type: string[];
  assigned_user_type: string;
  assigned_user_group_id?: number;
}

export interface WorkflowsResponse {
  workflows: WorkflowDashboard[];
  totalCount: number;
}

export interface TaskDashboard {
  task_id: number;
  task_name: string;
}

export interface TasksResponse {
  tasks: TaskDashboard[];
  totalCount: number;
}

export interface WorkflowStatusDashboard {
  workflow_status_id: number;
  workflow_status: string;
  created_datetime: string;
}

export interface WorkflowStatusesResponse {
  workflowStatuses: WorkflowStatusDashboard[];
  totalCount: number;
}

export interface ProgramType {
  program_type: string;
  program_type_id: number;
}

export interface ProgramTypesResponse {
  data: ProgramType[];
  success: boolean;
  error?: string;
}

export interface WorkflowDropdownItem {
  workflow_id: number;
  workflow_name: string;
  assigned_user_type: string;
  program_type: string[];
}

export interface WorkflowsDropdownResponse {
  data: WorkflowDropdownItem[];
  success: boolean;
  error?: string;
}

export interface TaskDropdownItem {
  task_id: number;
  task_name: string;
}

export interface TasksDropdownResponse {
  data: TaskDropdownItem[];
  success: boolean;
  error?: string;
}

export interface UserRole {
  role: string;
  role_id: number;
}

export interface UserRolesResponse {
  data: UserRole[];
  success: boolean;
  error?: string;
}

export interface ProgramTypeStatus {
  program_status: string;
  program_status_mapper_id: number;
  program_type: string;
}

export interface ProgramTypeStatusesResponse {
  data: ProgramTypeStatus[];
  success: boolean;
  error?: string;
}

export interface WorkflowAuditLog {
  pep_workflow_audit_log_id: number;
  workflow_id: number;
  action_type: string;
  action_datetime: string;
  action_by: string;
  workflow_data: any;
  metadata: any;
  triggers: any;
}

export interface ProgramStatusAuditLog {
  entity_id: number;
  metadata: any;
  request_id: string;
}

class WorkflowService {
  /**
   * Fetch workflows with pagination
   */
  async getWorkflows(
    offset: number,
    limit: number,
    hasuraToken: string
  ): Promise<WorkflowsResponse> {
    try {
      const response = await graphqlClient.query(
        GET_WORKFLOWS_DASHBOARD,
        { offset, limit },
        hasuraToken
      );

      if (!response.success || !response.data) {
        throw new Error(response.errors?.[0] || 'Failed to fetch workflows');
      }

      const workflows = response.data.pep_vw_workflow_dashboard || [];
      
      return {
        workflows,
        totalCount: workflows.length < limit ? offset + workflows.length : offset + workflows.length + 1
      };
    } catch (error) {
      console.error('Error fetching workflows:', error);
      throw error;
    }
  }

  /**
   * Search workflows with pagination
   */
  async searchWorkflows(
    searchField: string,
    searchValue: string,
    offset: number,
    limit: number,
    hasuraToken: string
  ): Promise<WorkflowsResponse> {
    try {
      let response;
      
      if (searchField === 'workflow_name') {
        const searchPattern = `%${searchValue}%`;
        response = await graphqlClient.query(
          SEARCH_WORKFLOWS_DASHBOARD,
          { 
            searchValue: searchPattern,
            offset, 
            limit 
          },
          hasuraToken
        );
      } else if (searchField === 'program_type') {
        response = await graphqlClient.query(
          SEARCH_WORKFLOWS_DASHBOARD_BY_PROGRAM,
          { 
            searchValue: searchValue.toUpperCase(),
            offset, 
            limit 
          },
          hasuraToken
        );
      } else if (searchField === 'assigned_user_type') {
        // Convert search value to user group ID if it's numeric, otherwise try to match by name
        const userGroupId = parseInt(searchValue);
        if (!isNaN(userGroupId)) {
          response = await graphqlClient.query(
            SEARCH_WORKFLOWS_DASHBOARD_BY_USER_GROUP,
            { 
              userGroupId,
              offset, 
              limit 
            },
            hasuraToken
          );
        } else {
          // If not numeric, return empty results
          return {
            workflows: [],
            totalCount: 0
          };
        }
      } else if (searchField === 'assigned_user_group') {
        // Handle multiple user group IDs (array)
        try {
          const userGroupIds = JSON.parse(searchValue);
          if (Array.isArray(userGroupIds) && userGroupIds.length > 0) {
            response = await graphqlClient.query(
              SEARCH_WORKFLOWS_DASHBOARD_BY_USER_GROUPS,
              { 
                userGroupIds,
                offset, 
                limit 
              },
              hasuraToken
            );
          } else {
            return {
              workflows: [],
              totalCount: 0
            };
          }
        } catch {
          return {
            workflows: [],
            totalCount: 0
          };
        }
      } else {
        // Default fallback
        const searchPattern = `%${searchValue}%`;
        response = await graphqlClient.query(
          SEARCH_WORKFLOWS_DASHBOARD,
          { 
            searchValue: searchPattern,
            offset, 
            limit 
          },
          hasuraToken
        );
      }

      if (!response.success || !response.data) {
        throw new Error(response.errors?.[0] || 'Failed to search workflows');
      }

      const workflows = response.data.pep_vw_workflow_dashboard || [];

      return {
        workflows,
        totalCount: workflows.length < limit ? offset + workflows.length : offset + workflows.length + 1
      };
    } catch (error) {
      console.error('Error searching workflows:', error);
      throw error;
    }
  }

  /**
   * Fetch tasks with pagination
   */
  async getTasks(
    offset: number,
    limit: number,
    hasuraToken: string
  ): Promise<TasksResponse> {
    try {
      const response = await graphqlClient.query(
        GET_TASKS_DASHBOARD,
        { offset, limit },
        hasuraToken
      );

      if (!response.success || !response.data) {
        throw new Error(response.errors?.[0] || 'Failed to fetch tasks');
      }

      const tasks = response.data.pep_v2_task || [];
      
      return {
        tasks,
        totalCount: tasks.length < limit ? offset + tasks.length : offset + tasks.length + 1
      };
    } catch (error) {
      console.error('Error fetching tasks:', error);
      throw error;
    }
  }

  /**
   * Search tasks with pagination
   */
  async searchTasks(
    searchValue: string,
    offset: number,
    limit: number,
    hasuraToken: string
  ): Promise<TasksResponse> {
    try {
      const searchPattern = `%${searchValue}%`;
      
      const response = await graphqlClient.query(
        SEARCH_TASKS_DASHBOARD,
        { 
          searchValue: searchPattern, 
          offset, 
          limit 
        },
        hasuraToken
      );

      if (!response.success || !response.data) {
        throw new Error(response.errors?.[0] || 'Failed to search tasks');
      }

      const tasks = response.data.pep_v2_task || [];

      return {
        tasks,
        totalCount: tasks.length < limit ? offset + tasks.length : offset + tasks.length + 1
      };
    } catch (error) {
      console.error('Error searching tasks:', error);
      throw error;
    }
  }

  /**
   * Fetch workflow statuses with pagination
   */
  async getWorkflowStatuses(
    offset: number,
    limit: number,
    hasuraToken: string
  ): Promise<WorkflowStatusesResponse> {
    try {
      const response = await graphqlClient.query(
        GET_WORKFLOW_STATUSES_DASHBOARD,
        { offset, limit },
        hasuraToken
      );

      if (!response.success || !response.data) {
        throw new Error(response.errors?.[0] || 'Failed to fetch workflow statuses');
      }

      const workflowStatuses = response.data.pep_workflow_status || [];
      
      return {
        workflowStatuses,
        totalCount: workflowStatuses.length < limit ? offset + workflowStatuses.length : offset + workflowStatuses.length + 1
      };
    } catch (error) {
      console.error('Error fetching workflow statuses:', error);
      throw error;
    }
  }

  /**
   * Search workflow statuses with pagination
   */
  async searchWorkflowStatuses(
    searchValue: string,
    offset: number,
    limit: number,
    hasuraToken: string
  ): Promise<WorkflowStatusesResponse> {
    try {
      const searchPattern = `%${searchValue}%`;
      
      const response = await graphqlClient.query(
        SEARCH_WORKFLOW_STATUSES_DASHBOARD,
        { 
          searchValue: searchPattern, 
          offset, 
          limit 
        },
        hasuraToken
      );

      if (!response.success || !response.data) {
        throw new Error(response.errors?.[0] || 'Failed to search workflow statuses');
      }

      const workflowStatuses = response.data.pep_workflow_status || [];

      return {
        workflowStatuses,
        totalCount: workflowStatuses.length < limit ? offset + workflowStatuses.length : offset + workflowStatuses.length + 1
      };
    } catch (error) {
      console.error('Error searching workflow statuses:', error);
      throw error;
    }
  }

  /**
   * Get workflow status by ID
   */
  async getWorkflowStatusById(
    id: number,
    hasuraToken: string
  ): Promise<WorkflowStatusDashboard | null> {
    try {
      const response = await graphqlClient.query(
        GET_WORKFLOW_STATUS_BY_ID,
        { id },
        hasuraToken
      );

      if (!response.success || !response.data) {
        throw new Error(response.errors?.[0] || 'Failed to fetch workflow status');
      }

      const workflowStatuses = response.data.pep_workflow_status || [];
      return workflowStatuses.length > 0 ? workflowStatuses[0] : null;
    } catch (error) {
      console.error('Error fetching workflow status by ID:', error);
      throw error;
    }
  }

  /**
   * Fetch program types
   */
  async getProgramTypes(hasuraToken: string): Promise<ProgramTypesResponse> {
    try {
      const response = await graphqlClient.query<{ pep_program_type: ProgramType[] }>(
        GET_PROGRAM_TYPES,
        {},
        hasuraToken
      );

      if (!response.success || !response.data) {
        return {
          data: [],
          success: false,
          error: response.errors?.[0] || 'Failed to fetch program types'
        };
      }

      return {
        data: response.data.pep_program_type || [],
        success: true
      };
    } catch (error: any) {
      console.error('Error fetching program types:', error);
      return {
        data: [],
        success: false,
        error: error.message || 'Failed to fetch program types'
      };
    }
  }

  /**
   * Fetch all workflows for dropdown
   */
  async getAllWorkflowsForDropdown(hasuraToken: string): Promise<WorkflowsDropdownResponse> {
    try {
      const response = await graphqlClient.query<{ pep_vw_workflow_dashboard: WorkflowDropdownItem[] }>(
        GET_ALL_WORKFLOWS_FOR_DROPDOWN,
        {},
        hasuraToken
      );

      if (!response.success || !response.data) {
        return {
          data: [],
          success: false,
          error: response.errors?.[0] || 'Failed to fetch workflows'
        };
      }

      // Filter out deleted workflows as an extra safeguard (query already filters, but ensure client-side)
      const workflows = (response.data.pep_vw_workflow_dashboard || []).filter(
        (w: any) => w.is_deleted === false || w.is_deleted === null || w.is_deleted === undefined
      );

      return {
        data: workflows,
        success: true
      };
    } catch (error: any) {
      console.error('Error fetching workflows for dropdown:', error);
      return {
        data: [],
        success: false,
        error: error.message || 'Failed to fetch workflows'
      };
    }
  }

  /**
   * Fetch all tasks for dropdown
   */
  async getAllTasksForDropdown(hasuraToken: string): Promise<TasksDropdownResponse> {
    try {
      const response = await graphqlClient.query<{ pep_v2_task: TaskDropdownItem[] }>(
        GET_ALL_TASKS_FOR_DROPDOWN,
        {},
        hasuraToken
      );

      if (!response.success || !response.data) {
        return {
          data: [],
          success: false,
          error: response.errors?.[0] || 'Failed to fetch tasks'
        };
      }

      return {
        data: response.data.pep_v2_task || [],
        success: true
      };
    } catch (error: any) {
      console.error('Error fetching tasks for dropdown:', error);
      return {
        data: [],
        success: false,
        error: error.message || 'Failed to fetch tasks'
      };
    }
  }

  /**
   * Fetch user roles
   */
  async getUserRoles(hasuraToken: string): Promise<UserRolesResponse> {
    try {
      const response = await graphqlClient.query<{ reference_user_role: UserRole[] }>(
        GET_USER_ROLES,
        {},
        hasuraToken
      );

      if (!response.success || !response.data) {
        return {
          data: [],
          success: false,
          error: response.errors?.[0] || 'Failed to fetch user roles'
        };
      }

      return {
        data: response.data.reference_user_role || [],
        success: true
      };
    } catch (error: any) {
      console.error('Error fetching user roles:', error);
      return {
        data: [],
        success: false,
        error: error.message || 'Failed to fetch user roles'
      };
    }
  }

  /**
   * Get workflow by ID
   */
  async getWorkflowById(
    userId: string,
    workflowId: string,
    token: string
  ) {
    try {
      const response = await apiClient.get(
        `/api/ngpep/${userId}/workflow/${workflowId}`,
        token
      );

      if (!response.success) {
        throw new Error(response.error || 'Failed to fetch workflow');
      }

      return response;
    } catch (error: any) {
      console.error('Error fetching workflow by ID:', error);
      throw error;
    }
  }

  /**
   * Delete workflow by ID
   */
  async deleteWorkflow(
    userId: string,
    workflowId: string | number,
    token: string
  ) {
    try {
      const response = await apiClient.delete(
        `/api/ngpep/${userId}/workflow/${workflowId}`,
        token
      );

      if (!response.success) {
        throw new Error(response.error || 'Failed to delete workflow');
      }

      return response;
    } catch (error: any) {
      console.error('Error deleting workflow:', error);
      throw error;
    }
  }

  /**
   * Fetch program type statuses
   */
  async getProgramTypeStatuses(hasuraToken: string): Promise<ProgramTypeStatusesResponse> {
    try {
      const response = await graphqlClient.query<{ pep_vw_program_type_status: ProgramTypeStatus[] }>(
        GET_PROGRAM_TYPE_STATUSES,
        {},
        hasuraToken
      );

      if (!response.success || !response.data) {
        return {
          data: [],
          success: false,
          error: response.errors?.[0] || 'Failed to fetch program type statuses'
        };
      }

      return {
        data: response.data.pep_vw_program_type_status || [],
        success: true
      };
    } catch (error: any) {
      console.error('Error fetching program type statuses:', error);
      return {
        data: [],
        success: false,
        error: error.message || 'Failed to fetch program type statuses'
      };
    }
  }

  /**
   * Fetch program types from dashboard view
   */
  async getProgramTypeDashboard(hasuraToken: string): Promise<{ data: string[]; success: boolean; error?: string }> {
    try {
      const response = await graphqlClient.query<{ pep_vw_program_type_dashboard: { program_type: string }[] }>(
        GET_PROGRAM_TYPE_DASHBOARD,
        {},
        hasuraToken
      );

      if (!response.success || !response.data) {
        return {
          data: [],
          success: false,
          error: response.errors?.[0] || 'Failed to fetch program types'
        };
      }

      // Extract unique program types
      const programTypes = response.data.pep_vw_program_type_dashboard
        .map(item => item.program_type)
        .filter((value, index, self) => value && self.indexOf(value) === index);

      return {
        data: programTypes,
        success: true
      };
    } catch (error: any) {
      console.error('Error fetching program types from dashboard:', error);
      return {
        data: [],
        success: false,
        error: error.message || 'Failed to fetch program types'
      };
    }
  }

  /**
   * Create a new workflow
   */
  async createWorkflow(
    userId: string,
    workflowData: {
      workflowName: string;
      programTypes: Array<{ programTypeId: number }>;
      triggers: Array<{ prevWorkflowId: number; programStatusMapperId: number }>;
      attempts: Array<{ taskId: number; attempt: number; waitTime: number }>;
      userGroupId: number;
    },
    token: string
  ) {
    try {
      const response = await apiClient.post(
        `/api/ngpep/${userId}/workflow`,
        workflowData,
        token
      );

      if (!response.success) {
        throw new Error(response.error || 'Failed to create workflow');
      }

      return response;
    } catch (error: any) {
      console.error('Error creating workflow:', error);
      throw error;
    }
  }

  /**
   * Update an existing workflow
   */
  async updateWorkflow(
    userId: string,
    workflowId: string,
    workflowData: {
      workflowName: string;
      programTypes: Array<{ programTypeId: number }>;
      triggers: Array<{ prevWorkflowId: number; programStatusMapperId: number }>;
      attempts: Array<{ taskId: number; attempt: number; waitTime: number }>;
      userGroupId: number;
    },
    token: string
  ) {
    try {
      const response = await apiClient.put(
        `/api/ngpep/${userId}/workflow/${workflowId}`,
        workflowData,
        token
      );

      if (!response.success) {
        throw new Error(response.error || 'Failed to update workflow');
      }

      return response;
    } catch (error: any) {
      console.error('Error updating workflow:', error);
      throw error;
    }
  }

  /**
   * Create a new program type with statuses
   */
  async createProgramType(
    userId: string,
    programType: string,
    programStatusIds: number[],
    token: string
  ) {
    try {
      const response = await apiClient.post(
        `/api/ngpep/${userId}/programTypeWithStatus`,
        {
          programType,
          programStatusIds
        },
        token
      );

      if (!response.success) {
        throw new Error(response.error || 'Failed to create program type');
      }

      return response;
    } catch (error: any) {
      console.error('Error creating program type:', error);
      throw error;
    }
  }

  /**
   * Update an existing program type with statuses
   */
  async updateProgramType(
    userId: string,
    programTypeId: string | number,
    programType: string,
    programStatusIds: number[],
    token: string
  ) {
    try {
      const response = await apiClient.put(
        `/api/ngpep/${userId}/programTypeWithStatus/${programTypeId}`,
        {
          programType,
          programStatusIds
        },
        token
      );

      if (!response.success) {
        throw new Error(response.error || 'Failed to update program type');
      }

      return response;
    } catch (error: any) {
      console.error('Error updating program type:', error);
      throw error;
    }
  }

  /**
   * Delete a program type by ID
   */
  async deleteProgramType(
    userId: string,
    programTypeId: string | number,
    token: string
  ) {
    try {
      const response = await apiClient.delete(
        `/api/ngpep/${userId}/programTypeWithStatus/${programTypeId}`,
        token
      );

      if (!response.success) {
        throw new Error(response.error || 'Failed to delete program type');
      }

      return response;
    } catch (error: any) {
      console.error('Error deleting program type:', error);
      throw error;
    }
  }

  /**
   * Create a new program status
   */
  async createProgramStatus(
    userId: string,
    data: { statusName: string },
    token: string
  ) {
    try {
      const response = await apiClient.post(
        `/api/ngpep/${userId}/programStatus`,
        { programStatus: data.statusName },
        token
      );

      if (!response.success) {
        throw new Error(response.error || 'Failed to create program status');
      }

      return response;
    } catch (error: any) {
      console.error('Error creating program status:', error);
      throw error;
    }
  }

  /**
   * Get a single program status by ID
   */
  async getProgramStatus(
    userId: string,
    programStatusId: string | number,
    token: string
  ) {
    try {
      const response = await apiClient.get(
        `/api/ngpep/${userId}/programStatus/${programStatusId}`,
        token
      );

      if (!response.success) {
        throw new Error(response.error || 'Failed to fetch program status');
      }

      return response.data;
    } catch (error: any) {
      console.error('Error fetching program status:', error);
      throw error;
    }
  }

  /**
   * Update a program status by ID
   */
  async updateProgramStatus(
    userId: string,
    programStatusId: string | number,
    data: { statusName: string },
    token: string
  ) {
    try {
      const response = await apiClient.put(
        `/api/ngpep/${userId}/programStatus/${programStatusId}`,
        { programStatus: data.statusName },
        token
      );

      if (!response.success) {
        throw new Error(response.error || 'Failed to update program status');
      }

      return response;
    } catch (error: any) {
      console.error('Error updating program status:', error);
      throw error;
    }
  }

  /**
   * Delete a program status by ID
   */
  async deleteProgramStatus(
    userId: string,
    programStatusId: string | number,
    token: string
  ) {
    try {
      const response = await apiClient.delete(
        `/api/ngpep/${userId}/programStatus/${programStatusId}`,
        token
      );

      if (!response.success) {
        throw new Error(response.error || 'Failed to delete program status');
      }

      return response;
    } catch (error: any) {
      console.error('Error deleting program status:', error);
      throw error;
    }
  }

  /**
   * Get workflow audit log
   */
  async getWorkflowAuditLog(hasuraToken: string, workflowId: number) {
    return await graphqlClient.query<{
      pep_workflow_audit_log: WorkflowAuditLog[];
      pep_workflow_audit_log_aggregate: { aggregate: { count: number } };
    }>(
      GET_WORKFLOW_AUDIT_LOG,
      { workflowId },
      hasuraToken
    );
  }

  /**
   * Get program status audit log
   */
  async getProgramStatusAuditLog(hasuraToken: string, entityId: number) {
    try {
      const result = await graphqlClient.query<{
        pep_state_status_audit_log: ProgramStatusAuditLog[];
        pep_state_status_audit_log_aggregate: { aggregate: { count: number } };
      }>(
        GET_PROGRAM_STATUS_AUDIT_LOG,
        { entityId },
        hasuraToken,
        'GetProgramStatusAuditLog',
        'POST'
      );

      if (!result.success) {
        throw new Error(result.errors?.[0] || 'Failed to fetch program status audit log');
      }

      return {
        logs: result.data?.pep_state_status_audit_log || [],
        total: result.data?.pep_state_status_audit_log_aggregate?.aggregate?.count || 0,
      };
    } catch (error: any) {
      console.error('Error fetching program status audit log:', error);
      throw error;
    }
  }

  /**
   * Get program type audit log
   */
  async getProgramTypeAuditLog(hasuraToken: string, entityId: number) {
    try {
      const result = await graphqlClient.query<{
        pep_state_status_audit_log: ProgramStatusAuditLog[];
        pep_state_status_audit_log_aggregate: { aggregate: { count: number } };
      }>(
        GET_PROGRAM_TYPE_AUDIT_LOG,
        { entityId },
        hasuraToken,
        'GetProgramTypeAuditLog',
        'POST'
      );

      if (!result.success) {
        throw new Error(result.errors?.[0] || 'Failed to fetch program type audit log');
      }

      return {
        logs: result.data?.pep_state_status_audit_log || [],
        total: result.data?.pep_state_status_audit_log_aggregate?.aggregate?.count || 0,
      };
    } catch (error: any) {
      console.error('Error fetching program type audit log:', error);
      throw error;
    }
  }

  /**
   * Get workflow status audit log
   */
  async getWorkflowStatusAuditLog(hasuraToken: string, entityId: number) {
    try {
      const result = await graphqlClient.query<{
        pep_state_status_audit_log: ProgramStatusAuditLog[];
        pep_state_status_audit_log_aggregate: { aggregate: { count: number } };
      }>(
        GET_WORKFLOW_STATUS_AUDIT_LOG,
        { entityId },
        hasuraToken,
        'GetWorkflowStatusAuditLog',
        'POST'
      );

      if (!result.success) {
        throw new Error(result.errors?.[0] || 'Failed to fetch workflow status audit log');
      }

      return {
        logs: result.data?.pep_state_status_audit_log || [],
        total: result.data?.pep_state_status_audit_log_aggregate?.aggregate?.count || 0,
      };
    } catch (error: any) {
      console.error('Error fetching workflow status audit log:', error);
      throw error;
    }
  }

  /**
   * Get task audit log
   */
  async getTaskAuditLog(hasuraToken: string, entityId: number) {
    try {
      const result = await graphqlClient.query<{
        pep_state_status_audit_log: ProgramStatusAuditLog[];
        pep_state_status_audit_log_aggregate: { aggregate: { count: number } };
      }>(
        GET_TASK_AUDIT_LOG,
        { entityId },
        hasuraToken,
        'GetTaskAuditLog',
        'POST'
      );

      if (!result.success) {
        throw new Error(result.errors?.[0] || 'Failed to fetch task audit log');
      }

      return {
        logs: result.data?.pep_state_status_audit_log || [],
        total: result.data?.pep_state_status_audit_log_aggregate?.aggregate?.count || 0,
      };
    } catch (error: any) {
      console.error('Error fetching task audit log:', error);
      throw error;
    }
  }
}

export const workflowService = new WorkflowService();
export default workflowService;
